// The Kontrakt annotation format - ADR-0015 of the entropie-dashboard project.
//
// A Kontrakt is a trackable commitment, to someone else or to oneself, phrased
// as a promise and followed over time. Declaring one is a property on a
// headline; membership is inherited by that headline's whole subtree. organice
// is an *editor* for this format, not a reader of its evaluations: the
// dashboard that consumes these annotations is deliberately read-only
// (ADR-0006), which leaves Emacs as the format's only authoring path. This
// module is the second one.
//
//   * Zertifizierung nach ISO 27001
//   :PROPERTIES:
//   :KONTRAKT_ID: iso-27001
//   :KONTRAKT_MIT: Zertifizierungsstelle
//   :KONTRAKT_ZUSAGE: Zertifikat erteilt, Abweichungen geschlossen
//   :END:
//   ** TODO [#A] Voraudit bestehen                                     :ziel:
//   DEADLINE: <2026-09-18 Fr>
//   :PROPERTIES:
//   :DOD: Voraudit ohne Hauptabweichung, Bericht liegt vor
//   :END:
//
// Ported from etc/entropie-org-eingabe.el.example (the Emacs authoring
// commands) and js/entropie-scan.mjs (the membership resolution). Where the
// two projects' prose and their parsers disagree, the parsers win - see
// resolveKontrakt.

import { attributedStringToRawText } from './export_org';
import { parseMarkupAndCookies } from './parse_org';
import { indexOfHeaderWithId } from './org_utils';

import { fromJS } from 'immutable';
import generateId from './id_generator';

/** Declares a Kontrakt on this headline; its subtree inherits membership. */
export const KONTRAKT_ID = 'KONTRAKT_ID';
/** The counterparty, or `selbst` for a promise to oneself. */
export const KONTRAKT_MIT = 'KONTRAKT_MIT';
/** One sentence: what will be true when this is fulfilled. */
export const KONTRAKT_ZUSAGE = 'KONTRAKT_ZUSAGE';
/** Other Kennungen, space- or comma-separated. Connections are mutual. */
export const KONTRAKT_VERBUNDEN = 'KONTRAKT_VERBUNDEN';
/** Start of the strand; otherwise the earliest date among its members. */
export const KONTRAKT_START = 'KONTRAKT_START';
/** Assigns *this* node to a strand declared elsewhere. Beats inheritance. */
export const KONTRAKT = 'KONTRAKT';
/** Definition-of-Done: the evidence by which a Ziel is recognised as reached. */
export const DOD = 'DOD';
/** The tag that makes a headline a Ziel. It does not inherit. */
export const ZIEL_TAG = 'ziel';

/** The four Wirkung dimensions, in the order of the data contract. */
export const WIRKUNG_DIMENSIONS = ['ZIEL', 'ERGEBNIS', 'FINANZ', 'AUSSEN'];

export const wirkungProperty = (dimension) => `WIRKUNG_${`${dimension}`.toUpperCase()}`;

// Umlauts are spelled out rather than stripped: the Kennung shows up in file
// names, URLs and JSON keys, where nothing belongs that a character set can
// misplace.
const UMLAUTS = [
  [/ä/g, 'ae'],
  [/ö/g, 'oe'],
  [/ü/g, 'ue'],
  [/ß/g, 'ss'],
];

/**
 * A heading turned into a Kennung: lowercase, ASCII, hyphenated.
 *
 * The Kennung is a promise to the future - it stands in the Org files *and* in
 * every exported file, so renaming one rewrites membership retroactively.
 */
export const kennung = (text) => {
  const lowered = `${text || ''}`.toLowerCase();
  const spelled = UMLAUTS.reduce((value, [pattern, replacement]) => {
    return value.replace(pattern, replacement);
  }, lowered);

  return spelled
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

/** The Kennungen in a `:KONTRAKT_VERBUNDEN:` value, in the order written. */
export const parseVerbunden = (value) =>
  `${value || ''}`.split(/[,;\s]+/).filter((entry) => entry.length > 0);

/** Back to a property value. Space-separated, as the Emacs command writes it. */
export const formatVerbunden = (kennungen) => (kennungen || []).join(' ');

/** `value` with `addition` appended, unless it is already there. */
export const addVerbunden = (value, addition) => {
  const existing = parseVerbunden(value);
  const trimmed = `${addition || ''}`.trim();
  if (!trimmed || existing.includes(trimmed)) return formatVerbunden(existing);

  return formatVerbunden([...existing, trimmed]);
};

/** A Wirkung override is an integer 0 to 3 - four steps a human can assign consistently. */
export const isValidWirkung = (value) => /^[0-3]$/.test(`${value}`.trim());

/** The raw text of a property on a header, or null when it carries none. */
export const propertyValue = (header, property) => {
  const item = header
    .get('propertyListItems')
    .find((candidate) => candidate.get('property') === property);
  if (!item) return null;

  const raw = attributedStringToRawText(item.get('value')).trim();
  return raw.length ? raw : null;
};

/**
 * `propertyListItems` with `property` set to `value` - appended when absent,
 * replaced in place when present, and removed when the value is empty.
 *
 * In place matters: the property drawer must stay directly under the headline
 * and keep its order, since both parsers read it positionally.
 */
export const setProperty = (propertyListItems, property, value) => {
  const trimmed = `${value === null || value === undefined ? '' : value}`.trim();
  const index = propertyListItems.findIndex((item) => item.get('property') === property);

  if (!trimmed) {
    return index === -1 ? propertyListItems : propertyListItems.delete(index);
  }

  const parsed = fromJS(parseMarkupAndCookies(trimmed));
  if (index === -1) {
    return propertyListItems.push(fromJS({ property, value: parsed, id: generateId() }));
  }

  return propertyListItems.setIn([index, 'value'], parsed);
};

/**
 * Which strand a header belongs to, and how it got there:
 *
 * - `explicit`   - its own `:KONTRAKT:`, the most outspoken statement there is
 * - `declared`   - it declares the strand itself, with `:KONTRAKT_ID:`
 * - `inherited`  - an ancestor declares it
 * - `none`       - no strand; the scan will file it under its system's collector
 *
 * This mirrors js/entropie-scan.mjs and lisp/entropie-scan.el exactly, and
 * deliberately *not* README-ORGMODE §3, which claims a `:KONTRAKT:` on the top
 * headline gives a whole file to a strand. Neither parser pushes `:KONTRAKT:`
 * onto the inheritance stack, so it colours one node and no descendants.
 */
export const resolveKontrakt = (headers, headerId) => {
  const target = indexOfHeaderWithId(headers, headerId);
  if (target === -1) return { kennung: null, origin: 'none' };

  // The declaration stack over outline depth, walked in document order - the
  // same computation as both parsers, so a node lands in the same strand here
  // as it does in the export.
  const stack = [];

  for (let index = 0; index <= target; index += 1) {
    const header = headers.get(index);
    const level = header.get('nestingLevel');

    while (stack.length && stack[stack.length - 1].level >= level) stack.pop();

    const inherited = stack.length ? stack[stack.length - 1].kennung : null;
    const declared = propertyValue(header, KONTRAKT_ID);
    if (declared) stack.push({ level, kennung: declared });

    if (index === target) {
      const own = propertyValue(header, KONTRAKT);
      if (own) return { kennung: own, origin: 'explicit' };
      if (declared) return { kennung: declared, origin: 'declared' };
      if (inherited) return { kennung: inherited, origin: 'inherited' };
    }
  }

  return { kennung: null, origin: 'none' };
};

// German on purpose, like the popup's labels: these are the format's own words
// (CONTEXT.md of the entropie-dashboard), and an English rendering would name
// something slightly different from the property underneath.
const ORIGIN_WORDS = {
  declared: 'hier erklärt',
  explicit: 'zugeordnet',
  inherited: 'geerbt',
};

/**
 * A one-line summary of where a headline stands, for the drawer button's
 * tooltip - so a reference into the void is visible while standing on the node,
 * rather than only in the export.
 */
export const strandSummary = (strand) =>
  strand && strand.kennung
    ? `Kontrakt: ${strand.kennung} (${ORIGIN_WORDS[strand.origin]})`
    : 'Kontrakt: kein Strang';

/** Every Kennung declared in these headers, in document order, without duplicates. */
export const declaredKennungen = (headers) => {
  const seen = [];
  headers.forEach((header) => {
    const declared = propertyValue(header, KONTRAKT_ID);
    if (declared && !seen.includes(declared)) seen.push(declared);
  });

  return seen;
};

/** Whether this header carries the `:ziel:` tag. The tag never inherits. */
export const isZiel = (header) =>
  header.getIn(['titleLine', 'tags']).some((tag) => `${tag}`.toLowerCase() === ZIEL_TAG);
